import "./EditExhibition.css";
import {EditExhibitionForm} from "./EditExhibitionForm.js";

import {useEffect, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";

import DashboardHeader from "../../components/dashboardHeader/DashboardHeader";
import LoadingState from "../../components/loadingState/LoadingState";
import {ErrorMessage} from "../../components/errorMessage/ErrorMessage";
import PrimaryLabel from "../../components/primaryLabel/PrimaryLabel";
import PrimaryInput from "../../components/primaryInput/PrimaryInput";
import PrimaryButton from "../../components/primaryButton/PrimaryButton";
import {MediaFormField, MediaFileContent} from "../../components/mediaFormField/MediaFormField";
import FileFormField from "../../components/fileFormField/FileFormField";

import {fetchExhibitionByID, editExhibition} from "../../services/api/Exhibition";
import {uploadFile, editFile, deleteFile} from "../../services/api/File";
import useAccessToken from "../../hooks/useToken";
import {useDashboardLocale} from "../../context/dashboard_locale";
import interruptAuth from "../../utils/Interrupt_auth";
import {capitalize} from "../../utils/strings";
import {createExhibitionFilePath, getFileExtension} from "../../utils/file";
import {copy} from "../../utils/copy";
import {displayDialogMessage} from "../../utils/display_dialog_message";

export default function EditExhibition() {
    const [searchParams] = useSearchParams();
    const exhibitionID = parseInt(searchParams.get("id"));
    const {token} = useAccessToken();
    const {strings} = useDashboardLocale();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [form, setForm] = useState(new EditExhibitionForm());
    const [errorMessage, setErrorMessage] = useState(null);

    const mediaFormFieldJSXs = form.mediaFiles.map(function (mediaFile) {
      const mediaFileContent = new MediaFileContent(
          mediaFile.priority,
          mediaFile.file,
          mediaFile.titleFile,
          mediaFile.originName
      );
      return (
          <MediaFormField
              key={mediaFileContent.id}
              mediaFileContent={mediaFileContent}
              accept="audio/*,video/*,image/*"
              fileNameValidationError={strings[mediaFile.errLocalizedKey] ?? mediaFile.remoteErrorMessage}
              deleteDisabled={form.mediaFiles.length <= 1}
              onChange={onChangeMediaFormField}
              onDelete={onDeleteMediaFormField}
          />
      );
    });

    const imageFormFieldJSXs = form.imageFiles.map(function(imageFile) {
        return (
            <FileFormField
                key={imageFile.id}
                title={strings[imageFile.localizedKey]}
                id={imageFile.id}
                fileName={imageFile.titleFile}
                errorMessage={strings[imageFile.errLocalizedKey] ?? imageFile.remoteErrorMessage}
                onChange={onChangeImageFile}
                required={true}
            />
        );
    });

    useEffect(function() {
        interruptAuth(token, navigate)
    }, []);

    useEffect(() => {
        if (errorMessage) {
            displayDialogMessage(capitalize(strings["error"]), errorMessage, strings["ok"]);
        }
    }, [errorMessage, strings]);

    useEffect(function() {
        if (!Number.isInteger(exhibitionID)) {
            const wrongParametersMessage = strings?.["wrong_parameters"] ?? "Invalid parameters";
            setErrorMessage(wrongParametersMessage);
            return;
        }
        setIsLoading(true);
        fetchExhibitionByID(exhibitionID)
          .then((res) => {
              const exhibitionResponse = res.data["response"];
              let editForm = new EditExhibitionForm();
              editForm.parseResponse(exhibitionResponse);
              setForm(editForm);
              setIsLoading(false);
          }).catch( err => {
              const errMessage = err?.response?.data?.["error_message"];
              if (errMessage) {
                  setErrorMessage(errMessage);
              }
              setIsLoading(false);
              throw err;
          })
    }, []);

    function onChangeImageFile({id, file}) {
      let modForm = copy(form);
      modForm.updateImageFile(id, file);
      modForm.imageFiles = modForm.imageFiles.map(imageFile => {
        if (imageFile.id !== id) {
          return imageFile;
        }
        return {...imageFile, remoteErrorMessage: null};
      });
      setForm(modForm);
      setErrorMessage(null);
    }

    function onChangeMediaFormField({id, file, titleFile, originName}) {
        let modForm = copy(form);
        modForm.updateMediaFile(id, file, titleFile, originName);
        modForm.mediaFiles = modForm.mediaFiles.map(mediaFile => {
          if (mediaFile.priority !== id) {
            return mediaFile;
          }
          return {...mediaFile, remoteErrorMessage: null};
        });
        setForm(modForm);
        setErrorMessage(null);
    }

    function onDeleteMediaFormField(id) {
      let modForm = copy(form);
      modForm.removeMediaFile(id);
      setForm(modForm);
      setErrorMessage(null);
    }

    function setRemoteFileError(fileType, fieldId, errorMessage) {
      setForm(currentForm => {
        const modForm = copy(currentForm);
        if (fileType === "media") {
          modForm.mediaFiles = modForm.mediaFiles.map(mediaFile => {
            if (mediaFile.priority !== fieldId) {
              return mediaFile;
            }
            return {...mediaFile, remoteErrorMessage: errorMessage};
          });
        } else {
          modForm.imageFiles = modForm.imageFiles.map(imageFile => {
            if (imageFile.id !== fieldId) {
              return imageFile;
            }
            return {...imageFile, remoteErrorMessage: errorMessage};
          });
        }
        return modForm;
      });
    }

    function onAddNewMediaField(e) {
      e.preventDefault();

      let modForm = copy(form);
      modForm.addNewMediaFile();
      setForm(modForm);
      setErrorMessage(null);
    }

    function onChangeClientCode(e) {
      let modForm = copy(form);
      modForm.clientCode = e.target.value;
      setErrorMessage(null);
      setForm(modForm);
    }

    function onChangeProductCode(e) {
      let modForm = copy(form);
      modForm.productCode = e.target.value;
      setErrorMessage(null);
      setForm(modForm);
    }

    function onFormSubmit(e) {
      e.preventDefault();

      function EditFileNetworkModel(fieldId, fileType, id, tag, path, title, priority, file) {
        return {
          fieldId: fieldId,
          fileType: fileType,
          id: id,
          tag: tag,
          path: path,
          title: title,
          priority: priority,
          file: file,
        }
      }

      function UploadFileNetworkModel(fieldId, fileType, tag, path, title, priority, file) {
        return {
            fieldId: fieldId,
            fileType: fileType,
            tag: tag,
            path: path,
            title: title,
            priority: priority,
            file: file,
        }
      }

      setErrorMessage(null);
      const validationError = form.validateFields()
      if (validationError) {
          setErrorMessage(capitalize(strings[validationError.localizedKey]))
          return
      }

      let editNetworkModel = {
        "id": exhibitionID,
        "client_code": form.clientCode,
        "product_code": form.productCode,
        "medias": []
      };

      const newMediaFileNetworkModels = form.mediaFiles
        .filter(mediaFile => {
          return mediaFile.id === -1
        })
        .map((mediaFile) => {
          const file = mediaFile.file;
          const fileName = mediaFile.titleFile;
          const ext = getFileExtension(file.name);
          const path = createExhibitionFilePath(form.clientCode, form.productCode, "media", fileName, ext);
          const priority = mediaFile.priority;
          const tag = mediaFile.tag;
          return UploadFileNetworkModel(priority, "media", tag, path, fileName, priority, file);
        });

      const editMediaFileNetworkModels = form.mediaFiles
        .filter(mediaFile => {
          return mediaFile.id !== -1
        })
        .map((mediaFile) => {
          const fileName = mediaFile.titleFile;
          const file = mediaFile.file;
          const tag = mediaFile.tag;
          const priority = mediaFile.priority;
          const ext = file ? getFileExtension(file.name) : getFileExtension(mediaFile.path);
          const path = createExhibitionFilePath(form.clientCode, form.productCode, "media", fileName, ext);
          return EditFileNetworkModel(priority, "media", mediaFile.id, tag, path, fileName, priority, file);
        });

      const editImageFileNetworkModel = form.imageFiles.map((imageFile) => {
        const fileName = imageFile.titleFile;
        const file = imageFile.file;
        const tag = imageFile.tag;
        const priority = imageFile.priority;
        const ext = file ? getFileExtension(file.name) : getFileExtension(imageFile.path);
        const path = createExhibitionFilePath(form.clientCode, form.productCode, "image", fileName, ext);
        return EditFileNetworkModel(imageFile.id, "image", imageFile.id, tag, path, fileName, priority, file);
      });

      const removeMediaIDs = form.removeMediaIDs;
      const editFileNetworkModels = editMediaFileNetworkModels.concat(editImageFileNetworkModel);

      console.log("---START-DEBUG---");
      console.log("new media files", newMediaFileNetworkModels)
      console.log("edit image file", editImageFileNetworkModel);
      console.log("remove media ids", removeMediaIDs);
      console.log("editNetworkModel", editNetworkModel);
      console.log("---END-DEBUG---");

      const uploadedFiles = newMediaFileNetworkModels.map(fileNetworkModel => {
         return uploadFile(
            token, 
            fileNetworkModel.path, 
            fileNetworkModel.title,
            fileNetworkModel.priority, 
            fileNetworkModel.file
          )
          .then(res => {
              const id = res.data["response"]["id"];
              const tag = fileNetworkModel.tag;
              return {
                  id: id,
                  tag: tag
              };
          })
          .then(({id, tag}) => {
              editNetworkModel["medias"].push({
                "file_id": id,
                "tag": tag,
              });
          })
          .catch(err => {
              const errMessage = err.response?.data?.["error_message"] ?? "File upload failed";
              setRemoteFileError(fileNetworkModel.fileType, fileNetworkModel.fieldId, errMessage);
              throw err;
          })
      })
      const editedFiles = editFileNetworkModels.map(fileNetworkModel => {
        return editFile(
          token, 
          fileNetworkModel.id, 
          fileNetworkModel.path, 
          fileNetworkModel.title,
          fileNetworkModel.tag,
          fileNetworkModel.priority, 
          fileNetworkModel.file
        )
        .then(res => {
            return res.data["response"];
        })
        .catch(err => {
            const errMessage = err.response?.data?.["error_message"] ?? "File upload failed";
            setRemoteFileError(fileNetworkModel.fileType, fileNetworkModel.fieldId, errMessage);
            throw err;
        })
      })
      const deletedFiles = removeMediaIDs.map(mediaFileID => {
        return deleteFile(token, mediaFileID)
      });

      setIsLoading(true)
      Promise.all([
          ...editedFiles,
          ...deletedFiles,
          ...uploadedFiles
        ])
        .then(() => {
          return editExhibition(token, editNetworkModel);
        })
        .then(() => {
          navigate("/dashboard/exhibitions");
        })
        .catch( err => console.log(err))
        .finally(() => {
          setIsLoading(false);
        });
    }

    let mainContent;
    if (isLoading) {
      mainContent = (
        <div className='flex flex-grow'>
           <LoadingState text={`${strings["loading"]}...`}/>
        </div>
      );
    } else if (!Number.isInteger(exhibitionID)) {
      mainContent = (
        <div className="h-full w-full flex flex-col justify-center">
          <ErrorMessage>{errorMessage}</ErrorMessage>
        </div>
      );
    } else {
      mainContent = (
        <form>
          <div className="flex flex-wrap justify-center items-center">
            <div className="container shadown-md bg-white text-left rounded px-8 pt-6 pb-8 mb-4">
              <h4 className="text-lg text-center font-bold c-primary">{capitalize(strings["edit_exhibition_title"])}</h4>
              <h4 className="text-md text-left font-bold c-primary mb-2">{strings["exhibition_info"]}</h4>
              <div className="mb-5">
                <PrimaryLabel>{strings["client_code"]}<span className={"text-red-500 font-medium"}>*</span></PrimaryLabel>
                  <PrimaryInput
                    name="client_code"
                    type="text"
                    placeholder={strings["client_code_prompt"]}
                    onChange={onChangeClientCode}
                  >
                  {form.clientCode}
                  </PrimaryInput>
              </div>
              <div className="mb-5">
                <PrimaryLabel>{strings["product_code"]}<span className={"text-red-500 font-medium"}>*</span></PrimaryLabel>
                <PrimaryInput
                  name="product_code"
                  type="text"
                  placeholder={strings["product_code_prompt"]}
                  onChange={onChangeProductCode}
                >
                  {form.productCode}
                </PrimaryInput>
              </div>
              <div className="mb-8">
                  <h4 className="text-md text-left font-bold c-primary mb-2">{capitalize(strings["languages"])}<span className={"text-red-500 font-medium"}>*</span></h4>
                  <div className="mb-3">
                      {mediaFormFieldJSXs}
                  </div>
                  <div>
                      <PrimaryButton
                          onClick={onAddNewMediaField}
                          disabled={!form.canAddNewMedia()}
                      >
                        {capitalize(strings["add_language"])}
                      </PrimaryButton>
                  </div>
              </div>
              <div className="mb-5">
                <h4 className="text-md text-left font-bold c-primary mb-2">{capitalize(strings["screens"])}</h4>
                <div>
                    {imageFormFieldJSXs}
                </div>
              </div>
              <div className="mt-4">
                <PrimaryButton
                    disabled={isLoading}
                    extraClassName="w-full"
                    onClick={onFormSubmit}
                    isLoading={isLoading}
                >
                  {capitalize(strings["save"])}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </form>
      );
    }

    return (
      <div className="flex flex-col flex-grow min-h-screen">
        <DashboardHeader />
        {mainContent}
      </div>
    );
}
